import { AutocompleteProps } from "@mui/material/Autocomplete";
import OptionSelect from "../../widgets/OptionSelect";
import SectionTitle from "../../widgets/SectionTitle";
import ActionButton from "../../widgets/ActionButton";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { usePricingEstimatorContext } from "./index";

function Select<Option, FormFieldValues extends FieldValues>({
  name,
  label,
  control,
  ...autocompleteProps
}: {
  name: Path<FormFieldValues>;
  label: string;
  control: Control<FormFieldValues>;
} & Omit<AutocompleteProps<Option, any, any, false>, "renderInput">) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <OptionSelect
          {...autocompleteProps}
          label={label}
          value={field.value}
          onChange={(_, newValue) => {
            field.onChange(newValue);
          }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
        />
      )}
    />
  );
}

function NumberField<FormFieldValues extends FieldValues>({
  name,
  label,
  control,
}: {
  name: Path<FormFieldValues>;
  label: string;
  control: Control<FormFieldValues>;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          fullWidth
          type="number"
          label={label}
          value={field.value ?? ""}
          onChange={(e) => {
            if (e.target.value === "") {
              field.onChange(null);
            } else {
              field.onChange(Number(e.target.value));
            }
          }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
        />
      )}
    />
  );
}

function Inputs() {
  const { form, factorsResult, options } = usePricingEstimatorContext();

  if (factorsResult.error) {
    return <Typography color="error">Error loading options</Typography>;
  }

  if (factorsResult.loading || !options) {
    return (
      <>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={56} />
      </>
    );
  }

  // Only the sheets that are switched on can be applied, grouped by kind and then by name.
  const factors = [...(factorsResult.data?.getPricingFactors ?? [])]
    .filter((factor) => factor.enabled)
    .sort(
      (left, right) =>
        left.sheetType.localeCompare(right.sheetType) ||
        left.label.localeCompare(right.label),
    );

  return (
    <Grid container spacing={2}>
      <Grid item xs={3}>
        <Select
          name="gIAColor"
          label="GIA Color"
          options={options.gIAColors}
          control={form.control}
        />
      </Grid>
      <Grid item xs={3}>
        <Select
          name="langermanColor"
          label="Langerman Color"
          options={options.langermanColors}
          control={form.control}
        />
      </Grid>
      <Grid item xs={3}>
        <Select
          name="flatColor"
          label="Flat Color"
          options={options.flatColors}
          control={form.control}
        />
      </Grid>
      <Grid item xs={3}>
        <Select
          name="meleeColor"
          label="Melee Color"
          options={options.meleeColors}
          control={form.control}
        />
      </Grid>
      <Grid item xs={3}>
        <Select
          name="clarity"
          label="Clarity"
          options={options.clarities}
          control={form.control}
        />
      </Grid>
      <Grid item xs={3}>
        <NumberField name="carat" label="Carat" control={form.control} />
      </Grid>
      <Grid item xs={6}>
        <Select
          multiple
          name="factors"
          label="Factors"
          options={factors}
          getOptionKey={(option) => option.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props}>
              <Chip
                label={option.sheetType.replace("Factor", "")}
                sx={{ mr: 1 }}
              />
              {option.label}
            </li>
          )}
          control={form.control}
        />
      </Grid>
    </Grid>
  );
}

function Actions() {
  const { form, mutationResult, factorsResult } = usePricingEstimatorContext();

  if (factorsResult.error) {
    return null;
  }

  if (factorsResult.loading) {
    return (
      <>
        <Skeleton variant="rounded" width={140} height={36} />
        <Skeleton variant="rounded" width={140} height={36} />
      </>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        disabled={!form.formState.isDirty || mutationResult.loading}
        onClick={() => form.reset()}
      >
        Reset Values
      </Button>
      <ActionButton
        disabled={!form.formState.isDirty || mutationResult.loading}
        type="submit"
      >
        Estimate Price
      </ActionButton>
    </>
  );
}

function Form({ onSubmit }: { onSubmit: () => void }) {
  return (
    <>
      <SectionTitle>Diamond Characteristics</SectionTitle>
      <form onSubmit={onSubmit}>
        <Stack direction="column" gap={2}>
          <Inputs />
          <Stack
            direction="row"
            sx={{
              alignSelf: "flex-end",
            }}
            gap={2}
          >
            <Actions />
          </Stack>
        </Stack>
      </form>
    </>
  );
}

export default Form;
